import fields from '@src/features/RiskAnalysis/components/fields/includes/fields.json';
import { fieldValidator } from '@src/shared/includes/validation';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Widget } from '@src/shared/ui/components/Widget';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { SelectInputMulti } from '@uhg-abyss/web/ui/SelectInputMulti';
import { Tabs as AbyssTabs } from '@uhg-abyss/web/ui/Tabs';
import { isEmpty, isNull, isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';

import { LineChart } from '../LineChart';
import { Table } from '../Table';
import { Styles } from './includes/styles';

const options = [
  {
    icon: <IconSymbol icon="timeline" />,
    label: 'Line Chart',
    value: 'LINE_CHART',
  },
  {
    icon: <IconSymbol icon="table_chart" />,
    label: 'Data Table',
    value: 'DATA_TABLE',
  },
];

/**
 * Tabs
 *
 * Displays the data in tbs broken down by Attribute Type and Record Type.
 *
 * @param assets
 * @param queries
 * @param submittedValues
 * @returns {Element}
 * @constructor
 */
export function Tabs({ assets = {}, queries = {}, submittedValues = {} }) {
  const [tabs, setTabs] = useState([]);
  const [currentChartType, setCurrentChartType] = useState('LINE_CHART');
  const [selectedEvents, setSelectedEvents] = useState(null);

  /**
   * Defines a list of tabs to render based off data returned from the api.
   */
  useEffect(() => {
    const attributeTypes = submittedValues?.attributeTypes;
    const recordType = submittedValues?.recordType;
    const dateRange = submittedValues?.dateRange;
    const frequency = submittedValues?.frequency;

    if (!isEmpty(attributeTypes) && !isEmpty(queries)) {
      const items = [];

      const theFrequency = assets?.ListChronoUnits?.data?.find((chronoUnit) => {
        return chronoUnit?.codeId === frequency;
      });

      attributeTypes.forEach((attributeType) => {
        const query = queries[`GetRiskTrendReport-${attributeType}`];
        if (!isUndefined(query)) {
          items.push({
            data: query?.data,
            label: String(`${attributeType} - ${recordType}`).toUpperCase(),
            subText: `${dayjs(dateRange?.start).format('MM/DD/YYYY')} - ${dayjs(dateRange?.end).format(
              'MM/DD/YYYY'
            )} - ${theFrequency?.codeDesc}`,
          });
        }
      });

      setTabs(items);
    }
  }, [queries]);

  const widgetMenuOptions = [
    {
      label: 'View as...',
      onChange: setCurrentChartType,
      radios: options,
      title: 'View as...',
      value: currentChartType,
    },
  ];

  return (
    <ErrorHandler location="src/pages/private/Dashboards/OperationsDashboard/components/Tabs/Tabs.jsx">
      <Styles>
        <AbyssTabs grow={false}>
          {tabs?.map((tab) => {
            return (
              <AbyssTabs.Tab key={tab?.label} label={tab?.label} subText={tab?.subText}>
                <Widget
                  description="Number of entities with each untrusted source over time."
                  menu={widgetMenuOptions || []}
                  title="Untrusted Sources"
                  withCard={false}
                >
                  <Grid>
                    {currentChartType === 'LINE_CHART' && (
                      <React.Fragment>
                        <Grid.Col span={{ xs: '75%' }}>
                          <LineChart
                            data={tab?.data}
                            eventTypes={assets?.ListEventTypes?.data}
                            selectedEvents={
                              isNull(selectedEvents)
                                ? tab?.data?.events?.map((event) => {
                                    return event?.eventId;
                                  })
                                : selectedEvents
                            }
                          />
                        </Grid.Col>
                        <Grid.Col span={{ xs: '25%' }}>
                          <SelectInputMulti
                            label="Events"
                            maxListHeight="400px"
                            onChange={setSelectedEvents}
                            options={tab?.data?.events?.map((event) => {
                              return {
                                label: event?.title,
                                value: event?.eventId,
                              };
                            })}
                            placeholder="Select Event(s)"
                            selectAll
                            validators={{
                              ...fields?.views?.validators,
                              ...{
                                validate: {
                                  customValidator: (value) => {
                                    return fieldValidator(fields?.views, value);
                                  },
                                },
                              },
                            }}
                            value={
                              isNull(selectedEvents)
                                ? tab?.data?.events?.map((event) => {
                                    return event?.eventId;
                                  })
                                : selectedEvents
                            }
                          />
                        </Grid.Col>
                      </React.Fragment>
                    )}
                    {currentChartType === 'DATA_TABLE' && (
                      <Grid.Col span={{ xs: '100%' }}>
                        <Table data={tab?.data} />
                      </Grid.Col>
                    )}
                  </Grid>
                </Widget>
              </AbyssTabs.Tab>
            );
          })}
        </AbyssTabs>
      </Styles>
    </ErrorHandler>
  );
}
